import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CustomTextDisplay } from './AI';
import useAIInsights from '../utils/useAIInsights';

const AIToggleButton = ({ 
  downloadData, 
  radioButtonState, 
  kpiName, 
  chartType, 
  vinShort,
  title = "AI | Automotive Intelligence"
}) => {
  const { generateAIInsights, isLoading, error } = useAIInsights();
  const [showAI, setShowAI] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);

  const handleToggleAI = async () => {
    if (!showAI) {
      // Show AI - generate insights
      try {
        console.log("✨ Generating AI insights...", {
          downloadData,
          radioButtonState,
          kpiName,
          chartType,
          vinShort
        });

        const insights = await generateAIInsights({
          downloadData,
          radioButtonState,
          kpiName,
          chartType,
          vinShort
        });

        setAiInsights(insights);
        setShowAI(true);
      } catch (err) {
        console.error("Failed to generate AI insights:", err);
        setAiInsights("Failed to generate AI insights. Please try again.");
        setShowAI(true);
      }
    } else {
      // Hide AI
      setShowAI(false);
    }
  };

  return (
    <div style={{ marginTop: '16px' }}>
      {/* AI Toggle Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-start', // ✅ Changed from 'flex-end' to 'flex-start' for left alignment
        marginBottom: showAI ? '16px' : '0' 
      }}>
        <button
          onClick={handleToggleAI}
          disabled={isLoading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#59636D',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: '140px',
            justifyContent: 'center'
          }}
          onMouseOver={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = '#4a5259';
              e.target.style.opacity = '0.9';
            }
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#59636D';
            e.target.style.opacity = '1';
          }}
        >
          {isLoading ? (
            <>
              <span 
                className="spinner-border spinner-border-sm" 
                role="status" 
                aria-hidden="true"
                style={{ width: '14px', height: '14px' }}
              />
              Generating...
            </>
          ) : (
            <>
              {showAI ? 'Hide AI Insights' : 'Generate AI Insights'}
            </>
          )}
        </button>
      </div>

      {/* AI Insights Display */}
      {showAI && (
        <div style={{ 
          animation: 'fadeIn 0.3s ease-in',
          marginTop: '8px'
        }}>
          {error ? (
            <div style={{ 
              textAlign: 'center', 
              color: '#dc3545', 
              padding: '16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #dee2e6'
            }}>
              ❌ {error}
            </div>
          ) : aiInsights ? (
            <CustomTextDisplay 
              title={title}
              value={aiInsights} 
              isValueStyled 
            />
          ) : (
            <div style={{ 
              textAlign: 'center', 
              color: '#666',
              padding: '16px'
            }}>
              No AI insights available
            </div>
          )}
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

AIToggleButton.propTypes = {
  downloadData: PropTypes.array.isRequired,
  radioButtonState: PropTypes.object,
  kpiName: PropTypes.string.isRequired,
  chartType: PropTypes.string.isRequired,
  vinShort: PropTypes.string.isRequired,
  title: PropTypes.string,
};

export default AIToggleButton;